import { useCallback, useMemo, useState } from 'react'
import { getTimezoneOffsetString, getTimezoneOptions, isValidIANATimezone } from 'src/helpers/timezones'

const DISPLAY_TIMEZONE_STORAGE_KEY = 'display-timezone'

function getBrowserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

function getStoredTimezone(): string | null {
  try {
    return localStorage.getItem(DISPLAY_TIMEZONE_STORAGE_KEY)
  } catch {
    return null
  }
}

function resolveInitialStored(): string | null {
  const stored = getStoredTimezone()
  if (stored && isValidIANATimezone(stored)) {
    return stored
  }
  return null
}

export function useTimezone() {
  const [storedTimezone, setStoredTimezone] = useState<string | null>(() => resolveInitialStored())

  const browserTimezone = getBrowserTimezone()
  const timezone = storedTimezone !== null ? storedTimezone : browserTimezone

  const setTimezone = useCallback((value: string) => {
    if (value === getBrowserTimezone()) {
      setStoredTimezone(null)
      try {
        localStorage.removeItem(DISPLAY_TIMEZONE_STORAGE_KEY)
      } catch {
        // ignore
      }
    } else if (value && isValidIANATimezone(value)) {
      setStoredTimezone(value)
      try {
        localStorage.setItem(DISPLAY_TIMEZONE_STORAGE_KEY, value)
      } catch {
        // ignore
      }
    }
  }, [])

  const displayTimezoneOptions = useMemo(() => {
    const baseOptions = getTimezoneOptions()
    let currentLocationLabel = 'Use your current location'
    try {
      if (browserTimezone && isValidIANATimezone(browserTimezone)) {
        const offsetStr = getTimezoneOffsetString(new Date(), browserTimezone)
        currentLocationLabel = `Use your current location (${offsetStr})`
      }
    } catch {
      // keep default label
    }
    const currentLocationOption = {
      value: browserTimezone,
      label: currentLocationLabel,
    }
    const rest = baseOptions.filter((o) => o.value !== browserTimezone)
    return [currentLocationOption, ...rest]
  }, [browserTimezone])

  const displayTimezoneOffsetString = useMemo(() => {
    try {
      return getTimezoneOffsetString(new Date(), timezone)
    } catch {
      return 'UTC'
    }
  }, [timezone])

  return {
    timezone,
    setTimezone,
    displayTimezoneOptions,
    displayTimezoneOffsetString,
    hasExplicitDisplayTimezone: storedTimezone !== null,
  }
}
